import React, { useState, useCallback } from 'react'
import { Input, Select, Button } from 'antd'
import { CalendarOutlined, SearchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import DatesPickerModal from '../../../../../pages/components/DatesPickerV2'
import styles from './index.module.css'

interface WatermarkOption {
  id: string
  name: string
}

interface FilterBarProps {
  watermarks: WatermarkOption[]
  onSearch: (filter: {
    keyword: string
    startTime?: number
    endTime?: number
    watermarkIds: string[]
  }) => void
  onReset: () => void
  onTrack?: (clickItem: string) => void
  disabled?: boolean
}

const FilterBar: React.FC<FilterBarProps> = ({
  watermarks,
  onSearch,
  onReset,
  onTrack,
  disabled = false
}) => {
  const [keyword, setKeyword] = useState('')
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >(undefined)
  const [selectedWatermarks, setSelectedWatermarks] = useState<string[]>([])
  const [datePickerVisible, setDatePickerVisible] = useState(false)

  // 日期弹窗默认值：过去6个月 ~ 今天
  const defaultDates: [dayjs.Dayjs, dayjs.Dayjs] = [
    dayjs().subtract(6, 'months').startOf('day'),
    dayjs().endOf('day')
  ]

  // 日期展示文案
  const startLabel = dateRange?.[0]?.format('YYYY.MM.DD') || ''
  const endLabel = dateRange?.[1]?.format('YYYY.MM.DD') || ''

  const handleDatesChange = useCallback(
    (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
      const ordered: [dayjs.Dayjs, dayjs.Dayjs] = startDate.isAfter(endDate)
        ? [endDate, startDate]
        : [startDate, endDate]
      setDateRange(ordered)
      setDatePickerVisible(false)
    },
    []
  )

  const handleSearch = useCallback(() => {
    if (onTrack) onTrack('beginSearch')
    onSearch({
      keyword: keyword.trim(),
      startTime: dateRange?.[0]?.startOf('day').valueOf(),
      endTime: dateRange?.[1]?.endOf('day').valueOf(),
      watermarkIds: selectedWatermarks
    })
  }, [keyword, dateRange, selectedWatermarks, onSearch, onTrack])

  const handleReset = useCallback(() => {
    if (onTrack) onTrack('reset')
    setKeyword('')
    setDateRange(undefined)
    setSelectedWatermarks([])
    onReset()
  }, [onReset, onTrack])

  return (
    <div className={styles.bar}>
      {/* 关键词搜索 */}
      <Input
        prefix={<SearchOutlined style={{ color: '#999' }} />}
        placeholder="搜索水印关键字、地点等..."
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onFocus={() => onTrack?.('searchContent')}
        onPressEnter={handleSearch}
        style={{ width: 240 }}
        disabled={disabled}
      />

      {/* 拍摄时间：点击触发自定义日期弹窗 */}
      <div className={styles.field}>
        <span className={styles.label}>拍摄时间：</span>
        <div
          className={`${styles.dateTrigger} ${
            dateRange ? styles.dateTriggerActive : ''
          } ${disabled ? styles.dateTriggerDisabled : ''}`}
          onClick={() => {
            if (!disabled) {
              if (onTrack) onTrack('choosePhotoDate')
              setDatePickerVisible(true)
            }
          }}
        >
          {dateRange ? (
            <span>
              {startLabel} ~ {endLabel}
            </span>
          ) : (
            <span className={styles.datePlaceholder}>开始日期 ~ 结束日期</span>
          )}
          <CalendarOutlined className={styles.calendarIcon} />
        </div>
      </div>

      {/* 拍摄水印 */}
      <div className={styles.field}>
        <span className={styles.label}>拍摄水印：</span>
        <Select
          mode="multiple"
          placeholder="全部"
          value={selectedWatermarks}
          onChange={setSelectedWatermarks}
          onFocus={() => onTrack?.('chooseWatermark')}
          style={{ width: 192 }}
          maxTagCount={1}
          disabled={disabled}
          allowClear
        >
          {watermarks.map((w) => (
            <Select.Option key={w.id} value={w.id}>
              {w.name}
            </Select.Option>
          ))}
        </Select>
      </div>

      <Button onClick={handleReset} disabled={disabled}>
        重置
      </Button>
      <Button
        type="primary"
        onClick={handleSearch}
        disabled={disabled}
        style={{ background: '#0070F4', borderColor: '#0070F4' }}
      >
        查询
      </Button>

      {/* 日期选择弹窗：复用团队端 isAllPhoto 开关绕过 VIP 日期限制
          （DatesPickerV2 内部判断均为 isVip || isAllPhoto） */}
      <DatesPickerModal
        visible={datePickerVisible}
        dates={dateRange || defaultDates}
        onChange={handleDatesChange}
        onCancel={() => setDatePickerVisible(false)}
        isAllPhoto={true}
      />
    </div>
  )
}

export default FilterBar
